import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import emailjs from 'emailjs-com';

const ModernContactForm = () => {
  const [selected, setSelected] = useState("individual");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    companyName: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = {};

    if (!formData.name.trim()) validationErrors.name = "Name is required.";
    if (!formData.email.trim()) validationErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) validationErrors.email = "Email address is invalid.";
    if (selected === "company" && !formData.companyName.trim()) validationErrors.companyName = "Company name is required.";
    if (!formData.message.trim()) validationErrors.message = "Please enter a message.";

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      try {
        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          {
            name: formData.name,
            email: formData.email,
            companyName: formData.companyName,
            message: formData.message,
          },
          process.env.REACT_APP_EMAILJS_USER_ID
        );

        setStatus({ message: "Email sent successfully!", type: "success" });
        setFormData({ name: "", email: "", companyName: "", message: "" });
        setSelected("individual");
      } catch (error) {
        console.error("Failed to send email:", error);
        setStatus({ message: "An error occurred. Please try again later.", type: "error" });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto rounded-2xl overflow-hidden pointer-events-auto border border-white/10 backdrop-blur-sm shadow-2xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative"
      >
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900/95 via-neutral-800/95 to-neutral-900/95 backdrop-blur-xl" />

        {/* Animated Border Gradient */}
        <div className="absolute inset-0 opacity-50">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 via-violet-500/20 to-indigo-500/20 animate-pulse" />
        </div>

        <form onSubmit={handleSubmit} className="relative p-6 space-y-5">
          {/* Header */}
          <div className="text-left mb-6">
            <h3 className="text-xl font-bold bg-gradient-to-r from-white via-indigo-100 to-violet-100 bg-clip-text text-transparent mb-2">
              Let's Start a Conversation
            </h3>
          </div>

          {/* Name input */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">
              Your Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              placeholder="John Doe"
              onChange={handleChange}
              className="w-full px-3 py-2 bg-neutral-800/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-300 text-sm"
            />
            {errors.name && <p className="text-red-400 mt-1 text-xs flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.name}
            </p>}
          </div>

          {/* Email input */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              placeholder="john@example.com"
              onChange={handleChange}
              className="w-full px-3 py-2 bg-neutral-800/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-300 text-sm"
            />
            {errors.email && <p className="text-red-400 mt-1 text-xs flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.email}
            </p>}
          </div>

          {/* Company/individual toggle */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">
              I'm reaching out as
            </label>
            <FormSelect selected={selected} setSelected={setSelected} />
          </div>

          {/* Company name */}
          <AnimatePresence initial={false}>
            {selected === "company" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <label className="block text-xs font-medium text-gray-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  placeholder="Acme Inc."
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-neutral-800/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-300 text-sm"
                />
                {errors.companyName && <p className="text-red-400 mt-1 text-xs flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.companyName}
                </p>}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Message */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">
              Your Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              placeholder="Tell me about your project..."
              onChange={handleChange}
              className="w-full px-3 py-2 bg-neutral-800/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-300 min-h-[80px] resize-none text-sm"
            />
            {errors.message && <p className="text-red-400 mt-1 text-xs flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.message}
            </p>}
          </div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="relative w-full py-3 rounded-lg font-semibold text-white overflow-hidden group text-sm"
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-600 bg-size-200 bg-pos-0 group-hover:bg-pos-100 transition-all duration-500" />

            {/* Shine Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" style={{ animation: 'shine 2s ease-in-out infinite' }} />
            </div>

            {/* Content */}
            <span className="relative flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 000 8v4a8 8 0 01-8-8z" />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </span>
          </motion.button>

          {/* Status Message */}
          <AnimatePresence>
            {status && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`p-3 rounded-lg border text-xs ${
                  status.type === "success"
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                    : "bg-red-500/10 border-red-500/30 text-red-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  {status.type === "success" ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                  {status.message}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </motion.div>
    </div>
  );
};

const FormSelect = ({ selected, setSelected }) => {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => setSelected("individual")}
        className={`flex-1 px-3 py-2 rounded-lg font-medium transition-all duration-300 relative overflow-hidden text-sm border ${
          selected === "individual"
            ? "text-white border-transparent"
            : "text-gray-400 hover:text-gray-300 border-white/20 hover:border-white/30 bg-neutral-800/30 hover:bg-neutral-800/50"
        }`}
      >
        {selected === "individual" && (
          <motion.div
            layoutId="formSelectBg"
            className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-violet-600"
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
        <span className="relative z-10 flex items-center justify-center gap-1.5">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
          Individual
        </span>
      </button>
      <button
        type="button"
        onClick={() => setSelected("company")}
        className={`flex-1 px-3 py-2 rounded-lg font-medium transition-all duration-300 relative overflow-hidden text-sm border ${
          selected === "company"
            ? "text-white border-transparent"
            : "text-gray-400 hover:text-gray-300 border-white/20 hover:border-white/30 bg-neutral-800/30 hover:bg-neutral-800/50"
        }`}
      >
        {selected === "company" && (
          <motion.div
            layoutId="formSelectBg"
            className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-violet-600"
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
        <span className="relative z-10 flex items-center justify-center gap-1.5">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
          </svg>
          Company
        </span>
      </button>
    </div>
  );
};

export default ModernContactForm;
